import { createFileRoute } from "@tanstack/react-router";
import { Suspense, useMemo } from "react";
import { useLocale, useTranslations } from "next-intl";
import { PageHeader } from "@/components/page-header";
import { VehiclesFilters } from "@/components/vehicles/vehicles-filters";
import { VehiclesTable } from "@/components/vehicles/vehicles-table";
import { useVehiclesSearch } from "@/hooks/use-vehicles-search";
import { useExport } from "@/hooks/use-export";
import { vehiclesExportColumns } from "@/lib/export/columns";
import { useRouter } from "@/i18n/navigation";
import type { ApiSearchVehicleItem } from "@/types/api-helpers";

export const Route = createFileRoute("/_dashboard/vehicles/")({ component: VehiclesPage });

function VehiclesContent() {
  const t = useTranslations("VehiclesPage");
  const tVehicleKind = useTranslations("VehicleKind");
  const tVehiclesTable = useTranslations("VehiclesTable");
  const locale = useLocale();
  const router = useRouter();
  const {
    data,
    isLoading,
    search,
    setSearch,
    kind,
    setKind,
    sorting,
    onSortingChange,
    paginationState,
    onPaginationChange,
    pageCount,
  } = useVehiclesSearch();

  const exportFilters = useMemo(() => ({
    ...(search ? { q: search } : {}),
    ...(kind !== "all" ? { kind } : {}),
  }), [search, kind]);

  const { exportData, progress: exportProgress, cancel: cancelExport } = useExport<ApiSearchVehicleItem>({
    endpoint: "/admin/search/vehicles",
    columns: vehiclesExportColumns,
    filters: exportFilters,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    headerTranslator: (key) => tVehiclesTable(key as any),
    translators: {
      locale,
      tKind: (key) =>
        tVehicleKind.has(key as "car") ? tVehicleKind(key as "car") : key,
    },
    filenamePrefix: t("exportFilename"),
    pdfTitle: t("title"),
  });

  return (
    <>
      <PageHeader title={t("title")} />
      <div className="flex flex-1 flex-col gap-4 p-4">
        <VehiclesFilters
          search={search}
          onSearchChange={setSearch}
          kind={kind}
          onKindChange={setKind}
          searchPlaceholder={t("searchPlaceholder")}
          onExport={exportData}
          exportProgress={exportProgress}
          onExportCancel={cancelExport}
        />
        <VehiclesTable
          data={data}
          sorting={sorting}
          onSortingChange={onSortingChange}
          pagination={paginationState}
          onPaginationChange={onPaginationChange}
          pageCount={pageCount}
          isLoading={isLoading}
          showAssociatedUser
          onRowClick={(row) => router.push(`/vehicles/${row.id}`)}
        />
      </div>
    </>
  );
}

function VehiclesPage() {
  return (
    <Suspense>
      <VehiclesContent />
    </Suspense>
  );
}
